/**
 * Hearts — server-authoritative rules engine (4 players, every player for themselves, lowest score wins).
 *
 * Rules implemented (toggles live in {@link HeartsConfig}):
 *  - 4 players, one 52-card deck, 13 cards each. There is no trump.
 *  - Before each round except every fourth, every player passes three cards: left, right, across, then hold.
 *    Passing is done one seat at a time (starting left of the dealer) and nothing is revealed until all four
 *    players have chosen, so no player can see another's choice.
 *  - The holder of the 2♣ leads it. Players must follow the led suit when able.
 *  - On the first trick nobody may play a heart or the Q♠ unless every card they hold is a heart or the Q♠.
 *  - Hearts cannot be led until a heart has been played ("broken"), unless the leader holds only hearts.
 *  - The highest card of the led suit wins the trick and leads the next one.
 *  - Scoring per round: every heart is 1 point, the Q♠ is 13. If one player takes all 26 points they "shoot the
 *    moon": they score 0 and every other player scores 26 (when `shootTheMoon` is on).
 *  - The match ends when a player reaches `targetScore` (and the lowest total is unique) or after `maxRounds`.
 *    The lowest total wins; equal totals share a place.
 */
import { buildDeck, parseCard, removeCards, sortHand, type CardId } from '../../core/cards.js';
import { checkBool, checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface HeartsConfig {
  /** Match ends when a player reaches this total (and the lowest total is unique). */
  targetScore: number;
  /** Hard cap on rounds so a match always terminates. */
  maxRounds: number;
  shootTheMoon: boolean;
  roundEndDelayMs: number;
  trickEndDelayMs: number;
}

export type HeartsPhase = 'PASSING' | 'PLAYING' | 'TRICK_END' | 'ROUND_END' | 'MATCH_END';
export type PassDirection = 'left' | 'right' | 'across' | 'hold';

export interface HeartsTrickPlay {
  seat: number;
  card: CardId;
}

export interface HeartsCompletedTrick {
  leader: number;
  plays: HeartsTrickPlay[];
  winner: number;
}

export interface HeartsRoundResult {
  /** Points each seat scored this round (after any shoot-the-moon adjustment). */
  scores: number[];
  /** Seat that shot the moon, or null. */
  moon: number | null;
}

export interface HeartsState {
  config: HeartsConfig;
  players: string[];
  round: number;
  dealer: number;
  phase: HeartsPhase;
  hands: CardId[][];
  /** Cards each seat has chosen to pass this round (null until chosen). Hidden from other players. */
  passChoices: (CardId[] | null)[];
  /** Cards each seat received in the pass (visible to that seat only). */
  received: CardId[][];
  turn: number;
  trick: HeartsTrickPlay[];
  tricks: HeartsCompletedTrick[];
  heartsBroken: boolean;
  /** Penalty points each seat has taken so far this round (public information). */
  pointsTaken: number[];
  totals: number[];
  roundResults: HeartsRoundResult[];
}

export type HeartsAction = { type: 'pass'; cards: CardId[] } | { type: 'play'; card: CardId };

export type HeartsLegal =
  | { kind: 'pass'; count: number; direction: PassDirection }
  | { kind: 'play'; cards: CardId[] }
  | { kind: 'none' };

export interface HeartsView {
  gameType: 'HEARTS';
  config: HeartsConfig;
  seat: number | null;
  phase: HeartsPhase;
  round: number;
  dealer: number;
  turn: number;
  hand: CardId[];
  handCounts: number[];
  passDirection: PassDirection;
  /** Which seats have already chosen their pass (the cards themselves are never shown). */
  passed: boolean[];
  /** Cards this seat received in the pass this round. */
  received: CardId[];
  heartsBroken: boolean;
  trick: HeartsTrickPlay[];
  lastTrick: HeartsCompletedTrick | null;
  tricks: HeartsCompletedTrick[];
  pointsTaken: number[];
  roundResults: HeartsRoundResult[];
  totals: number[];
}

const DEFAULTS: HeartsConfig = {
  targetScore: 100,
  maxRounds: 16,
  shootTheMoon: true,
  roundEndDelayMs: 5000,
  trickEndDelayMs: 900,
};

const SEATS = 4;
export const PASS_COUNT = 3;
const TWO_CLUBS = '2C';
export const QUEEN_SPADES = 'QS';
const DIRECTIONS: PassDirection[] = ['left', 'right', 'across', 'hold'];

export const heartsPassDirection = (round: number): PassDirection => DIRECTIONS[(round - 1) % 4]!;

/** Penalty value of a card: hearts 1, Q♠ 13. */
export function heartsCardPoints(card: CardId): number {
  if (card === QUEEN_SPADES) return 13;
  return parseCard(card).suit === 'H' ? 1 : 0;
}

export function heartsTrickWinner(plays: readonly HeartsTrickPlay[]): number {
  const lead = parseCard(plays[0]!.card).suit;
  let best = plays[0]!;
  for (const p of plays.slice(1)) {
    const c = parseCard(p.card);
    if (c.suit === lead && c.rank > parseCard(best.card).rank) best = p;
  }
  return best.seat;
}

export function heartsLegalCards(
  hand: readonly CardId[],
  trick: readonly HeartsTrickPlay[],
  ctx: { firstTrick: boolean; heartsBroken: boolean },
): CardId[] {
  const cards = hand.map(parseCard);
  if (trick.length === 0) {
    if (ctx.firstTrick) return hand.includes(TWO_CLUBS) ? [TWO_CLUBS] : [...hand];
    const nonHearts = cards.filter((c) => c.suit !== 'H');
    if (ctx.heartsBroken || nonHearts.length === 0) return [...hand];
    return nonHearts.map((c) => c.id);
  }
  const lead = parseCard(trick[0]!.card).suit;
  const followers = cards.filter((c) => c.suit === lead);
  if (followers.length > 0) return followers.map((c) => c.id);
  if (ctx.firstTrick) {
    // Void on the first trick: no penalty cards unless there is nothing else to play.
    const safe = hand.filter((c) => heartsCardPoints(c) === 0);
    if (safe.length > 0) return safe;
  }
  return [...hand];
}

/** Ordering of how much a card is worth passing away (higher = pass it). Shared by timeouts and bots. */
export function heartsPassDanger(card: CardId, hand: readonly CardId[]): number {
  const c = parseCard(card);
  const suitCount = hand.filter((h) => parseCard(h).suit === c.suit).length;
  if (card === QUEEN_SPADES) return 100;
  if (c.suit === 'S') {
    // A♠ and K♠ are dangerous while the Q♠ is still out there, unless we hold many spades to protect them.
    if (c.rank >= 13) return suitCount <= 5 ? 90 + c.rank - 13 : 20;
    return c.rank >= 12 ? 0 : -5;
  }
  if (c.suit === 'H') return 40 + c.rank;
  // High cards in short suits are liabilities (they will be forced to win tricks), low cards are safe.
  return c.rank + (suitCount <= 3 ? 12 - suitCount * 2 : 0) - 4;
}

export function heartsChoosePass(hand: readonly CardId[]): CardId[] {
  return [...hand].sort((a, b) => heartsPassDanger(b, hand) - heartsPassDanger(a, hand)).slice(0, PASS_COUNT);
}

function passTarget(seat: number, direction: PassDirection): number {
  if (direction === 'left') return (seat + 1) % SEATS;
  if (direction === 'right') return (seat + 3) % SEATS;
  if (direction === 'across') return (seat + 2) % SEATS;
  return seat;
}

function startPlaying(state: HeartsState, events: EngineEvent[]): void {
  state.phase = 'PLAYING';
  state.trick = [];
  state.tricks = [];
  state.heartsBroken = false;
  state.pointsTaken = [0, 0, 0, 0];
  state.turn = state.hands.findIndex((h) => h.includes(TWO_CLUBS));
  events.push({ type: 'playStart', leader: state.turn });
}

function deal(state: HeartsState, rng: Rng, events: EngineEvent[]): void {
  const deck = shuffle(buildDeck(), rng);
  const hands: CardId[][] = [[], [], [], []];
  deck.forEach((c, i) => hands[i % SEATS]!.push(c));
  state.hands = hands.map((h) => sortHand(h));
  state.passChoices = [null, null, null, null];
  state.received = [[], [], [], []];
  state.trick = [];
  state.tricks = [];
  state.heartsBroken = false;
  state.pointsTaken = [0, 0, 0, 0];
  events.push({ type: 'dealt', round: state.round, dealer: state.dealer, counts: state.hands.map((h) => h.length), direction: heartsPassDirection(state.round) });
  if (heartsPassDirection(state.round) === 'hold') {
    startPlaying(state, events);
  } else {
    state.phase = 'PASSING';
    state.turn = (state.dealer + 1) % SEATS;
  }
}

function lowestFirst(totals: readonly number[]): Standing[] {
  const sorted = totals.map((score, seat) => ({ seat, score })).sort((a, b) => a.score - b.score);
  const out: Standing[] = [];
  sorted.forEach((s, i) => {
    const prev = out[i - 1];
    out.push({ seat: s.seat, score: s.score, place: prev && prev.score === s.score ? prev.place : i + 1 });
  });
  return out;
}

export const heartsEngine: GameEngine<HeartsConfig, HeartsState, HeartsAction, HeartsView, HeartsLegal> = {
  gameType: 'HEARTS',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('targetScore', c.targetScore, 20, 500);
    checkInt('maxRounds', c.maxRounds, 1, 40);
    checkBool('shootTheMoon', c.shootTheMoon);
    checkInt('roundEndDelayMs', c.roundEndDelayMs, 0, 60000);
    checkInt('trickEndDelayMs', c.trickEndDelayMs, 0, 10000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Hearts needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const state: HeartsState = {
      config,
      players,
      round: 1,
      dealer: rng.int(SEATS),
      phase: 'PASSING',
      hands: [],
      passChoices: [],
      received: [],
      turn: 0,
      trick: [],
      tricks: [],
      heartsBroken: false,
      pointsTaken: [0, 0, 0, 0],
      totals: [0, 0, 0, 0],
      roundResults: [],
    };
    deal(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'pass' && Array.isArray(raw.cards) && raw.cards.length === PASS_COUNT && raw.cards.every((c) => typeof c === 'string' && c.length <= 4)) {
      return { type: 'pass', cards: raw.cards as CardId[] };
    }
    if (raw.type === 'play' && typeof raw.card === 'string' && raw.card.length <= 4) return { type: 'play', card: raw.card };
    throw new EngineError('INVALID_ACTION', 'Unknown Hearts action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'Match is over');
    assertEngine(seat === state.turn && (state.phase === 'PASSING' || state.phase === 'PLAYING'), 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];

    if (action.type === 'pass') {
      assertEngine(state.phase === 'PASSING', 'INVALID_PHASE', 'Not passing cards now');
      const hand = state.hands[seat]!;
      assertEngine(new Set(action.cards).size === PASS_COUNT, 'INVALID_CARD', 'Choose three different cards');
      assertEngine(action.cards.every((c) => hand.includes(c)), 'INVALID_CARD', 'Card not in hand');
      state.passChoices[seat] = [...action.cards];
      // Only the fact that this seat has chosen is public.
      events.push({ type: 'passChosen', seat });
      if (state.passChoices.every((c) => c !== null)) {
        const direction = heartsPassDirection(state.round);
        // Remove every pass first, then deal the cards to their receivers, so no card is lost or duplicated.
        for (let s = 0; s < SEATS; s++) removeCards(state.hands[s]!, state.passChoices[s]!);
        for (let s = 0; s < SEATS; s++) {
          const to = passTarget(s, direction);
          state.hands[to]!.push(...state.passChoices[s]!);
          state.received[to] = [...state.passChoices[s]!];
        }
        state.hands = state.hands.map((h) => sortHand(h));
        events.push({ type: 'passComplete', direction });
        startPlaying(state, events);
      } else {
        state.turn = (seat + 1) % SEATS;
      }
      return events;
    }

    assertEngine(state.phase === 'PLAYING', 'INVALID_PHASE', 'Not in play phase');
    const hand = state.hands[seat]!;
    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
    const ctx = { firstTrick: state.tricks.length === 0, heartsBroken: state.heartsBroken };
    assertEngine(heartsLegalCards(hand, state.trick, ctx).includes(action.card), 'ILLEGAL_MOVE', 'That card is not allowed now');
    removeCards(hand, [action.card]);
    state.trick.push({ seat, card: action.card });
    events.push({ type: 'cardPlayed', seat, card: action.card });
    if (parseCard(action.card).suit === 'H' && !state.heartsBroken) {
      state.heartsBroken = true;
      events.push({ type: 'heartsBroken', seat });
    }
    if (state.trick.length < SEATS) {
      state.turn = (seat + 1) % SEATS;
      return events;
    }
    const winner = heartsTrickWinner(state.trick);
    const points = state.trick.reduce((n, p) => n + heartsCardPoints(p.card), 0);
    state.pointsTaken[winner]! += points;
    state.tricks.push({ leader: state.trick[0]!.seat, plays: state.trick, winner });
    events.push({ type: 'trickWon', seat: winner, trickNo: state.tricks.length, points });
    state.phase = 'TRICK_END';
    state.turn = winner;
    return events;
  },

  getActors(state) {
    return state.phase === 'PASSING' || state.phase === 'PLAYING' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (seat !== state.turn) return { kind: 'none' };
    if (state.phase === 'PASSING') return { kind: 'pass', count: PASS_COUNT, direction: heartsPassDirection(state.round) };
    if (state.phase === 'PLAYING') {
      return { kind: 'play', cards: heartsLegalCards(state.hands[seat]!, state.trick, { firstTrick: state.tricks.length === 0, heartsBroken: state.heartsBroken }) };
    }
    return { kind: 'none' };
  },

  getTimeoutAction(state, seat) {
    if (state.phase === 'PASSING') return { type: 'pass', cards: heartsChoosePass(state.hands[seat]!) };
    const legal = heartsLegalCards(state.hands[seat]!, state.trick, { firstTrick: state.tricks.length === 0, heartsBroken: state.heartsBroken });
    // Lowest-ranked legal card, preferring cards that carry no penalty.
    const best = [...legal].sort((a, b) => heartsCardPoints(a) - heartsCardPoints(b) || parseCard(a).rank - parseCard(b).rank)[0]!;
    return { type: 'play', card: best };
  },

  getView(state, seat) {
    return {
      gameType: 'HEARTS',
      config: state.config,
      seat,
      phase: state.phase,
      round: state.round,
      dealer: state.dealer,
      turn: state.turn,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      passDirection: heartsPassDirection(state.round),
      passed: state.passChoices.map((c) => c !== null),
      received: seat === null ? [] : [...(state.received[seat] ?? [])],
      heartsBroken: state.heartsBroken,
      trick: state.phase === 'TRICK_END' ? [...(state.tricks.at(-1)?.plays ?? [])] : [...state.trick],
      lastTrick: state.tricks.at(-1) ?? null,
      tricks: state.tricks.map((t) => ({ ...t, plays: [...t.plays] })),
      pointsTaken: [...state.pointsTaken],
      roundResults: state.roundResults.map((r) => ({ scores: [...r.scores], moon: r.moon })),
      totals: [...state.totals],
    };
  },

  getSystemStep(state) {
    if (state.phase === 'TRICK_END') return { kind: 'collectTrick', delayMs: state.config.trickEndDelayMs };
    if (state.phase === 'ROUND_END') return { kind: 'nextRound', delayMs: state.config.roundEndDelayMs };
    return null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'TRICK_END') {
      state.trick = [];
      if (state.tricks.length < 13) {
        state.phase = 'PLAYING';
        events.push({ type: 'trickCollected', leader: state.turn });
        return events;
      }
      const cfg = state.config;
      const moonSeat = state.pointsTaken.findIndex((p) => p === 26);
      const moon = cfg.shootTheMoon && moonSeat >= 0 ? moonSeat : null;
      const scores = moon === null ? [...state.pointsTaken] : state.pointsTaken.map((_, s) => (s === moon ? 0 : 26));
      state.roundResults.push({ scores, moon });
      state.totals = state.totals.map((t, s) => t + scores[s]!);
      events.push({ type: 'roundEnd', round: state.round, scores, moon, totals: [...state.totals] });
      const lowest = Math.min(...state.totals);
      const uniqueLowest = state.totals.filter((t) => t === lowest).length === 1;
      const reached = state.totals.some((t) => t >= cfg.targetScore);
      if ((reached && uniqueLowest) || state.round >= cfg.maxRounds) {
        state.phase = 'MATCH_END';
        events.push({ type: 'matchEnd', standings: lowestFirst(state.totals) });
      } else {
        state.phase = 'ROUND_END';
      }
      return events;
    }
    if (state.phase === 'ROUND_END') {
      state.round++;
      state.dealer = (state.dealer + 1) % SEATS;
      deal(state, rng, events);
      return events;
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return lowestFirst(state.totals);
  },

  getRoundsCompleted: (state) => state.roundResults.length,
};
